/**
 * Equipment Tracker - Auth Context (Midway)
 * @author Omid Zaeri (zaerio)
 *
 * Authentication flow:
 * 1. Fetch ID token directly from midway-auth.amazon.com/SSO
 * 2. Decode JWT to extract user alias
 * 3. Call GET /users/me with the JWT in Authorization header
 * 4. If user exists in DB, they're authenticated and registered
 *
 * Impersonation ("View As"):
 * Admins can switch the UI to show what another user would see.
 * The real user is preserved for API auth; only the displayed
 * user/isAdmin/isRegionalManager change.
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getIdentityToken, extractAliasFromToken } from '../utils/midwayAuth';
import { getUser, setMidwayToken as setMidwayTokenApi } from '../services/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [realUser, setRealUser] = useState(null);
  const [viewAsUser, setViewAsUser] = useState(null);
  const [viewAsNonUser, setViewAsNonUser] = useState(false);
  const [nonUserAlias, setNonUserAlias] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [midwayToken, setMidwayToken] = useState(null);

  const authenticate = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const token = await getIdentityToken();
      if (!token) return;
      setMidwayToken(token);
      const alias = extractAliasFromToken(token);
      if (!alias) throw new Error('Could not extract alias from Midway token');
      setMidwayTokenApi(token);
      const userData = await getUser('me', token);
      setRealUser(userData);
    } catch (err) {
      console.error('Auth error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { authenticate(); }, [authenticate]);

  // The "effective" user — either the impersonated user or the real user
  const user = viewAsUser || realUser;

  const realIsAdmin = realUser?.alias === 'zaerio' || realUser?.role === 'admin' || realUser?.tier === 'Manager';
  const isAdmin = viewAsUser
    ? (viewAsUser.alias === 'zaerio' || viewAsUser.role === 'admin' || viewAsUser.tier === 'Manager')
    : realIsAdmin;
  const isRegionalManager = user?.tier === 'Regional Manager';
  const isManager = user?.tier === 'Manager' || isRegionalManager;
  const isImpersonating = !!viewAsUser;

  const setViewAs = (userData) => { setViewAsUser(userData); setViewAsNonUser(false); setNonUserAlias(null); };
  const clearViewAs = () => { setViewAsUser(null); setViewAsNonUser(false); setNonUserAlias(null); };
  const setNonUserView = (alias) => { setViewAsUser(null); setViewAsNonUser(true); setNonUserAlias(alias || null); };

  const logout = () => {
    setRealUser(null);
    setViewAsUser(null);
    setMidwayToken(null);
    window.location.href = 'https://midway-auth.amazon.com/logout';
  };

  return (
    <AuthContext.Provider value={{
      user, realUser, isAdmin, realIsAdmin, isRegionalManager, isManager,
      isImpersonating, viewAsNonUser, nonUserAlias, setViewAs, clearViewAs, setNonUserView,
      loading, error, logout, midwayToken, reAuthenticate: authenticate,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
